/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import { useState, useRef, useMemo } from 'react'
import { Marker, MapRef } from 'react-map-gl'
import Box from '@mui/material/Box'
import Sales from '@/components/wrappers/sales'
import SalesDetails from '@/components/wrappers/sales/detail'
import Fade from '@mui/material/Fade'
import { useAppState } from '@/contexts/StateContext'
import { DetailSalesItems, SalesItem } from '@/types/sales'
import { useEffect } from 'react'
import { useAppTimerSetters } from '@/contexts/TimerContext'
import mapboxgl from 'mapbox-gl'
import { ZOOM_LEVELS, LONGITUDE_DELTA } from '@/utils/constant'
import useSupercluster from 'use-supercluster'
import { Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import LMap from '@/components/common/LMap'
import { DateTime } from 'luxon'
import { TIMEZONE } from '@/utils/constant'

const popupRight = 871
const arrowTopThreshold = 124
const arrowBottomThreshold = 300

const MarkerIcon = styled('div', {
  shouldForwardProp: (prop) => !['color', 'radius'].includes(prop as string)
})((props: { color: string; radius: number }) => ({
  backgroundColor: props.color,
  borderRadius: '50%',
  boxSizing: 'border-box',
  height: props.radius,
  width: props.radius,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer'
}))

const CenterPane = () => {
  const mapWrapperRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MapRef>(null)
  const { currentCohort, salesTimeframeState } = useAppState()
  const { setPlaying, setIdleTimerProgress } = useAppTimerSetters()
  const firstMarkerRef = useRef<HTMLDivElement>(null)
  const [svgSize, setSvgSize] = useState<{
    width: number
    height: number
    x: number
    y: number
  } | null>(null)
  const [mapViewState, setMapViewState] = useState({
    latitude: currentCohort.latitude,
    longitude: currentCohort.longitude - LONGITUDE_DELTA[currentCohort.type],
    zoom: ZOOM_LEVELS[currentCohort.type]
  })
  const now = DateTime.local().setZone(TIMEZONE[currentCohort.timezone])
  const [salesTimeframe, setSalesTimeframe] = useState('QTD')
  const salesState = salesTimeframeState[salesTimeframe]
  const isBetween7and11 = now.hour > 0 && now.hour < 11 && now.weekday === 1

  useEffect(() => {
    if (isBetween7and11) {
      setSalesTimeframe('QTD')
    } else {
      setSalesTimeframe('WTD')
    }
  }, [isBetween7and11, salesTimeframeState])

  const [selectedSalesItem, setSelectedSalesItem] = useState<SalesItem | null>(null)
  const [selectedSalesItems, setSelectedSalesItems] = useState<DetailSalesItems[]>([])
  const [clusterIndex, setClusterIndex] = useState<number>(0)
  const [activeDot, setActiveDot] = useState<null | number>(null)
  const [bounds, setBounds] = useState<[number, number, number, number]>([0, 0, 0, 0])
  const [scroolProgress, setScroolProgress] = useState<[boolean, boolean]>([false, false])
  const [lId, setLId] = useState<number>()

  useEffect(() => {
    setMapViewState({
      latitude: currentCohort.latitude,
      longitude: currentCohort.longitude - LONGITUDE_DELTA[currentCohort.type],
      zoom: ZOOM_LEVELS[currentCohort.type]
    })
  }, [currentCohort])

  useEffect(() => {
    if (!salesState?.DETAIL_SALES_ARRAY) {
      setSelectedSalesItem(null)
      setSelectedSalesItems([])
      setClusterIndex(0)
      setSvgSize(null)
    }
  }, [salesState?.DETAIL_SALES_ARRAY])

  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current.getMap()
      const bounds = map?.getBounds()
      if (bounds) {
        setBounds(bounds.toArray().flat() as [number, number, number, number])
      }
    }
  }, [mapRef.current])

  const points = useMemo(() => {
    if (!salesState?.DETAIL_SALES_ARRAY) return []
    return salesState.DETAIL_SALES_ARRAY.map((point) => ({
      ...point,
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: currentCohort.type === 'division' 
          ? point.geometry.coordinates 
          : point.division_coordinates
      }
    }))
  }, [salesState?.DETAIL_SALES_ARRAY, currentCohort])

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom: Math.ceil(mapViewState.zoom),
    options: {
      radius: currentCohort.type === 'division' ? 50 : 1,
      maxZoom: Math.floor(mapViewState.zoom) + 1
    }
  })

  useEffect(() => {
    if (clusters.length === 0) return
    const { idx } = clusters.reduce(
      (acc, cluster, id) => {
        const leaves = cluster.properties.cluster
          ? supercluster?.getLeaves(cluster.id as number, Infinity)
          : [cluster]
        leaves?.forEach((leaf) => {
          if (leaf.properties.sales.sale_cohort_rank < acc.minSaleCohortRank) {
            acc.minSaleCohortRank = leaf.properties.sales.sale_cohort_rank
            acc.idx = id
          }
        })
        return acc
      },
      { minSaleCohortRank: Infinity, idx: 0 }
    )
    setLId(idx)
  }, [clusters])

  useEffect(() => {
    const options = {
      maxZoom: ZOOM_LEVELS[currentCohort.type],
      // padding: { top: 80, bottom: 80, left: 650, right: 900 },
      bearing: 0,
      linear: true
    }
    if (mapRef.current) {
      if (points.length && lId !== undefined) {
        let minLat = 90
        let maxLat = -90
        let minLng = 180
        let maxLng = -180

        points.forEach((point: DetailSalesItems) => {
          const lat = point.geometry?.coordinates[1]
          const lng = point.geometry?.coordinates[0]

          if (lat !== undefined) {
            minLat = Math.min(minLat, lat)
            maxLat = Math.max(maxLat, lat)
          }

          if (lng !== undefined) {
            minLng = Math.min(minLng, lng)
            maxLng = Math.max(maxLng, lng)
          }
        })

        const coordinates: mapboxgl.LngLatBoundsLike = [
          { lat: minLat, lng: minLng },
          { lat: maxLat, lng: maxLng }
        ]

        // padding is in pixels. we can assign the left and right to be the pixel sizes of the existing elements
        mapRef.current.fitBounds(coordinates, options)

        const map = mapRef.current.getMap()
        if (map) {
          map.on('moveend', () => {
            if (firstMarkerRef.current) {
              handleClickMarker(
                clusters[lId],
                lId
              )({ currentTarget: firstMarkerRef.current } as React.MouseEvent<HTMLDivElement>)
            }
          })
        }
      } else {
        const { latitude, longitude } = currentCohort
        const coordinates: mapboxgl.LngLatBoundsLike = [
          { lat: latitude, lng: longitude },
          { lat: latitude, lng: longitude }
        ]
        mapRef.current.fitBounds(coordinates, options)
      }
    }
  }, [mapRef.current, clusters, currentCohort, points, lId])

  const handleClickMarker =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (dot: any, idx: number, manualTrigger?: boolean) => (evt: React.MouseEvent<HTMLDivElement>) => {
      if (mapWrapperRef.current) {
        if (manualTrigger) {
          setIdleTimerProgress(0)
          setPlaying(false)
        }

        const mapRect = mapWrapperRef.current.getBoundingClientRect()
        const markerRect = evt.currentTarget.getBoundingClientRect()
        const mapTop = mapRect.top

        const scaleRanges = [
          { min: 40, max: 50, scale: 2.4 },
          { min: 50, max: 55, scale: 2.1 },
          { min: 55, max: 60, scale: 1.9 },
          { min: 60, max: 65, scale: 1.7 },
          { min: 65, max: 70, scale: 1.5 },
          { min: 70, max: 75, scale: 1.45 },
          { min: 75, max: 80, scale: 1.9 },
          { min: 80, max: 90, scale: 1.25 },
          { min: 90, max: 100, scale: 1.07 }
        ]

        let scaleFactor = 1.0

        for (const range of scaleRanges) {
          if (mapTop >= range.min && mapTop < range.max) {
            scaleFactor = range.scale
            break
          }
        }

        let offsetTop: number, offsetRight: number

        if (scaleFactor === 1.0) {
          offsetTop = Math.round(markerRect.top - mapRect.top)
          offsetRight = Math.round(mapRect.right - markerRect.right)
        } else {
          offsetTop = Math.round((markerRect.top - mapRect.top) * scaleFactor)
          offsetRight = Math.round((mapRect.right - markerRect.right) * scaleFactor)
        }

        const svgWidth = offsetRight - 872

        setSvgSize({
          width: svgWidth,
          height:
            offsetTop < arrowTopThreshold
              ? arrowBottomThreshold - offsetTop
              : offsetTop < arrowBottomThreshold
              ? offsetTop - 5
              : offsetTop - arrowTopThreshold,
          x: popupRight,
          y: offsetTop < arrowTopThreshold ? offsetTop : arrowTopThreshold
        })

        if (dot?.properties?.cluster && supercluster) {
          const leaves = supercluster
            .getLeaves(dot.id as number, Infinity)
            .sort(
              (a, b) => a.properties.sales.sale_cohort_rank - b.properties.sales.sale_cohort_rank
            )
          setSelectedSalesItems(leaves as DetailSalesItems[])
          setScroolProgress([true, false])
        } else {
          setSelectedSalesItems([dot])
          setScroolProgress([true, true])
        }
        setClusterIndex(0)

        setTimeout(() => {
          window.requestAnimationFrame(() => {
            const svg = document.getElementById('triangleSvg')
            if (svg) {
              svg.innerHTML = ''
              const dotY =
                offsetTop < arrowTopThreshold
                  ? 16
                  : offsetTop < arrowBottomThreshold
                  ? offsetTop - arrowTopThreshold + 14
                  : offsetTop - arrowTopThreshold

              const dot1 = { x: 4, y: dotY }
              const dot2 = { x: svgWidth, y: 0 }
              const dot3 = { x: svgWidth, y: 176 }

              const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon')
              polygon.setAttribute(
                'points',
                `${dot1.x},${dot1.y} ${dot2.x},${dot2.y} ${dot3.x},${dot3.y}`
              )
              polygon.setAttribute('fill', 'rgba(38,54,81,0.85)')
              polygon.style.strokeWidth = '0'

              svg.append(polygon)
            }
          })
        }, 10)
        setActiveDot(idx)
      }
    }

  const handleCloseTooltip = () => {
    setSvgSize(null)
    setActiveDot(null)
    const svg = document.getElementById('triangleSvg')
    if (svg) svg.innerHTML = ''
  }

  useEffect(() => {
    if (selectedSalesItems !== null) {
      if (selectedSalesItems[clusterIndex]?.properties?.sales) {
        setSelectedSalesItem(selectedSalesItems[clusterIndex].properties.sales)
      }
      setScroolProgress([clusterIndex === 0, clusterIndex === selectedSalesItems.length - 1])
    }
  }, [clusterIndex, selectedSalesItems])

  const handleClickPrev = () => {
    if (selectedSalesItems.length > 1 && clusterIndex > 0) {
      setScroolProgress([false, false])
      setClusterIndex(clusterIndex - 1)
    }
  }

  const handleClickNext = () => {
    if (selectedSalesItems.length > 1 && clusterIndex < selectedSalesItems.length - 1) {
      setScroolProgress([false, false])
      setClusterIndex(clusterIndex + 1)
    }
  }

  const markers = useMemo(() => {
    if (!clusters.length && lId === undefined) return null
    return clusters.map((dot, idx) => {
      const [longitude, latitude] = dot.geometry.coordinates
      const { point_count: pointCount } = dot.properties
      const cZoom = Math.ceil(mapViewState.zoom)
      const cRadius = !pointCount ? 30 : 40 + (Math.min(pointCount, 100) / 25) * cZoom
      return (
        <Marker key={`salesPoint-${idx}`} latitude={latitude} longitude={longitude}>
          <MarkerIcon
            color="rgba(28,209,161,0.85)"
            radius={cRadius}
            className={`pulsatingDot ${activeDot === idx ? 'running' : ''}`}
            role="button"
            tabIndex={-1}
            ref={idx === lId ? firstMarkerRef : undefined}
            onClick={handleClickMarker(dot, idx, true)}
          >
            <Typography
              style={{
                fontSize: `20px`,
                fontWeight: 700
              }}
            >
              {pointCount || 1}
            </Typography>
          </MarkerIcon>
        </Marker>
      )
    })
  }, [clusters, lId, activeDot])

  return (
    <Box position="relative" ref={mapWrapperRef} width="100%" height="100%">
      <Sales
        data={salesState}
        salesTimeframe={salesTimeframe}
        setSalesTimeframe={setSalesTimeframe}
      />
      <Box sx={{ height: '100%' }}>
        <LMap
          mapViewState={mapViewState}
          style={{ width: '100%', position: 'absolute', top: 0, bottom: 0 }}
          setMapViewState={setMapViewState}
          mapRef={mapRef}
        >
          {markers}
        </LMap>
      </Box>
      {svgSize && selectedSalesItem && (
        <Fade in={!!svgSize} timeout={100} mountOnEnter unmountOnExit>
          <div>
            <SalesDetails
              svgSize={svgSize}
              onClose={handleCloseTooltip}
              salesDetail={selectedSalesItem}
              mapWrapperRef={mapWrapperRef}
              onPrev={handleClickPrev}
              onNext={handleClickNext}
              scrollProgress={scroolProgress}
            />
          </div>
        </Fade>
      )}
      <Typography
        sx={{
          fontFamily: 'mabry-pro',
          fontSize: '16px',
          position: 'absolute',
          textAlign: 'center',
          width: '100%',
          bottom: 40
        }}
      >
        WTD sales
      </Typography>
    </Box>
  )
}

export default CenterPane
